import { graphql, useStaticQuery } from "gatsby"

const normalizeArrayField = (value) => {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") {
          return item
        }

        if (item?.value) {
          return item.value
        }

        if (item?.title) {
          return item.title
        }

        return null
      })
      .filter(Boolean)
  }

  if (typeof value === "string") {
    return [value]
  }

  if (value?.value) {
    return [value.value]
  }

  if (value?.title) {
    return [value.title]
  }

  return []
}

const normalizeSynonymsField = (value) => {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value
      .flatMap((item) => normalizeSynonymsField(item))
      .filter(Boolean)
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  }

  if (value?.value || value?.title) {
    return normalizeSynonymsField(value.value || value.title)
  }

  return []
}

const normalizeSearchText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeSearchText(item)).join(" ")
  }

  if (value && typeof value === "object") {
    return normalizeSearchText(Object.values(value))
  }

  if (value === null || value === undefined) {
    return ""
  }

  return String(value).toLowerCase().trim()
}

export const useAllStories = (locale) => {
  const { allCosmicjsStories } = useStaticQuery(graphql`
    query {
      allCosmicjsStories {
        edges {
          node {
            slug
            id
            title
            type
            locale
            created_at
            published_at
            modified_at
            metadata {
              simplified_version {
                created_at
                modified_at
              }
              story_keywords
              story_job_title
              story_video_url
              story_company
              story_location
              story_personality_filter
              story_interests_filter
              story_identity_filter
              story_required_education_filter
              story_personal_photo {
                imgix_url
              }
              story_featured_career {
                title
                metadata {
                  career_synonyms
                  career_keywords
                }
                slug
              }
              story_school_reference {
                slug
                title
              }
              story_multi_career_references {
                title
                metadata {
                  career_synonyms
                  career_keywords
                }
              }
              story_quote
            }
          }
        }
      }
    }
  `)

  const selectedLocale = locale || "en-US"

  return allCosmicjsStories.edges
    .filter((story) => story.node.locale === selectedLocale)
    .map((story, index) => {
      const featuredCareer = story.node.metadata.story_featured_career
      const multiCareerReferences = story.node.metadata.story_multi_career_references || []
      const schoolReferences = story.node.metadata.story_school_reference || []

      const featuredCareerSynonyms = normalizeSynonymsField(featuredCareer?.metadata?.career_synonyms)
      const featuredCareerKeywordSynonyms = normalizeSynonymsField(featuredCareer?.metadata?.career_keywords)
      const multiCareerSynonyms = multiCareerReferences.flatMap((career) => normalizeSynonymsField(career?.metadata?.career_synonyms))
      const multiCareerKeywordSynonyms = multiCareerReferences.flatMap((career) => normalizeSynonymsField(career?.metadata?.career_keywords))
      const referencedCareerTitles = [
        featuredCareer?.title,
        ...multiCareerReferences.map((career) => career?.title),
      ].filter(Boolean)
      const referencedSchoolTitles = schoolReferences
        .map((school) => school?.title)
        .filter(Boolean)

      // Kept as separate arrays (rather than merged into one blob) so downstream search/ranking
      // code can tell which source a match came from and weight/prioritize each source
      // differently - e.g. an exact match on a true career_synonyms value should be able to
      // outrank a match that only came from a broader career_keywords value or a career title.
      const storyCareerSynonyms = Array.from(new Set([
        ...featuredCareerSynonyms,
        ...multiCareerSynonyms,
      ]))

      const storyCareerKeywordSynonyms = Array.from(new Set([
        ...featuredCareerKeywordSynonyms,
        ...multiCareerKeywordSynonyms,
      ]))

      const storyCareerTitles = Array.from(new Set(referencedCareerTitles))

      const storyCareerSynonymsText = normalizeSearchText(storyCareerSynonyms)
      const storyCareerKeywordsText = normalizeSearchText(storyCareerKeywordSynonyms)
      const storyCareerTitlesText = normalizeSearchText(storyCareerTitles)
      const storySchoolTitlesText = normalizeSearchText(referencedSchoolTitles)

      const storySearchableText = normalizeSearchText([
        story.node.metadata.story_job_title,
        story.node.title,
        story.node.metadata.story_keywords,
        story.node.metadata.story_company,
        story.node.metadata.story_location,
        storyCareerSynonyms,
        storyCareerKeywordSynonyms,
        storyCareerTitles,
        referencedSchoolTitles,
      ])

      return {
        key: index,
        objectID: story.node.id,
        slug: story.node.slug,
        id: story.node.id,
        title: story.node.title,
        type: story.node.type,
        locale: story.node.locale,
        created_at: story.node.created_at,
        published_at: story.node.published_at,
        modified_at: story.node.modified_at,
        simplified_version_created_at: story.node.metadata.simplified_version?.created_at,
        simplified_version_modified_at: story.node.metadata.simplified_version?.modified_at,
        story_keywords: story.node.metadata.story_keywords,
        story_job_title: story.node.metadata.story_job_title,
        story_video_url: story.node.metadata.story_video_url,
        story_company: story.node.metadata.story_company,
        story_location: story.node.metadata.story_location,
        story_personality_filter: normalizeArrayField(story.node.metadata.story_personality_filter),
        story_interests_filter: normalizeArrayField(story.node.metadata.story_interests_filter),
        story_identity_filter: normalizeArrayField(story.node.metadata.story_identity_filter),
        story_required_education_filter: normalizeArrayField(story.node.metadata.story_required_education_filter),
        story_personal_photo: story.node.metadata.story_personal_photo?.imgix_url,
        story_featured_career_slug: featuredCareer?.slug,
        story_career_synonyms: storyCareerSynonyms,
        story_career_synonyms_text: storyCareerSynonymsText,
        story_career_keywords: storyCareerKeywordSynonyms,
        story_career_keywords_text: storyCareerKeywordsText,
        story_career_titles: storyCareerTitles,
        story_career_titles_text: storyCareerTitlesText,
        story_school_reference: schoolReferences,
        story_school_reference_titles: referencedSchoolTitles,
        story_school_reference_titles_text: storySchoolTitlesText,
        story_searchable_text: storySearchableText,
        story_quote: story.node.metadata.story_quote,
      }
    })
}
